import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import PaginationBar from "../components/ui/PaginationBar";
import auditService from "../services/auditService";
import { ScrollText } from "lucide-react";
import { parseApiDateTime } from "../utils/datetime";

// Rótulos amigáveis das ações + cor do badge.
const ACTION_LABELS = {
  login: "Login",
  create: "Criação",
  update: "Edição",
  delete: "Exclusão",
  comment: "Comentário",
};
const ACTION_BADGE = {
  login: "bg-slate-100 text-slate-700",
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  comment: "bg-amber-100 text-amber-700",
};
const ENTITY_LABELS = {
  auth: "Autenticação",
  ticket: "Chamado",
  user: "Usuário",
  permission: "Permissões",
};

const fmtDateTime = (v) => {
  const d = parseApiDateTime(v);
  if (!d) return "";
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Auditoria = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const activeTenantName = useMemo(
    () => localStorage.getItem("active_tenant_name") || "",
    []
  );

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await auditService.list({
          skip: page * pageSize,
          limit: pageSize,
          action: action || undefined,
          entity: entity || undefined,
          search: search || undefined,
        });
        if (!alive) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotal(data?.total || 0);
      } catch (err) {
        if (alive) {
          setError(err.response?.data?.detail || "Erro ao carregar a auditoria.");
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [page, pageSize, action, entity, search]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const selectCls =
    "rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auditoria</h1>
          <p className="text-gray-500">
            Registro de quem fez o quê e quando
            {activeTenantName ? ` — ${activeTenantName}` : ""}.
          </p>
        </div>

        {/* Filtros */}
        <div className="rounded-lg border bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Ação</label>
              <select
                className={selectCls}
                value={action}
                onChange={(e) => {
                  setPage(0);
                  setAction(e.target.value);
                }}
              >
                <option value="">Todas</option>
                {Object.entries(ACTION_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Entidade</label>
              <select
                className={selectCls}
                value={entity}
                onChange={(e) => {
                  setPage(0);
                  setEntity(e.target.value);
                }}
              >
                <option value="">Todas</option>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <form onSubmit={onSearchSubmit} className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Buscar</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className={`${selectCls} flex-1`}
                  placeholder="Usuário ou descrição..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <Button type="submit">
                  Buscar
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Tabela */}
        <div className="rounded-lg border bg-white">
          {error && (
            <div className="m-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="px-4 py-3 font-semibold w-40">Quando</th>
                  <th className="px-4 py-3 font-semibold w-48">Usuário</th>
                  <th className="px-4 py-3 font-semibold w-28">Ação</th>
                  <th className="px-4 py-3 font-semibold">Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      Carregando...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      Nenhum evento de auditoria encontrado.
                    </td>
                  </tr>
                ) : (
                  items.map((a) => (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                        {fmtDateTime(a.created_at)}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {a.user_name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            ACTION_BADGE[a.action] || "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {ACTION_LABELS[a.action] || a.action}
                        </span>
                        <span className="ml-2 text-xs text-gray-400">
                          {ENTITY_LABELS[a.entity] || a.entity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{a.description || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-3">
            <PaginationBar
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPage(0);
                setPageSize(s);
              }}
            />
          </div>
        </div>

        <p className="flex items-center gap-1.5 text-xs text-gray-400">
          <ScrollText size={14} /> A trilha registra login, criação/edição/exclusão de
          chamados e usuários, e mudanças de permissões.
        </p>
      </div>
    </Layout>
  );
};

export default Auditoria;
